/**
 * DeleteItemPopover Component
 *
 * This component provides a generic and reusable popover for delete confirmations.
 * It is designed to be flexible and customizable, allowing you to specify different icons,
 * button labels, and confirmation messages to suit various contexts within the application.
 *
 * Props:
 * - onDelete: Function to be called when the delete action is confirmed.
 * - icon: Optional ReactNode to display as the icon for the delete button. Defaults to TrashIcon.
 * - deleteLabel: Optional string to customize the label of the delete confirmation button.
 * - cancelLabel: Optional string to customize the label of the cancel button.
 * - confirmationMessage: Optional string to customize the confirmation message displayed in the popover.
 * - className: Optional string to apply additional CSS classes for styling.
 *
 * Usage:
 * Import and use this component wherever a delete confirmation is needed. Pass the appropriate props
 * to customize its appearance and behavior.
 *
 * Example:
 * <DeleteItemPopover
 *   onDelete={handleDelete}
 *   icon={<CustomIcon />}
 *   deleteLabel="Remove"
 *   cancelLabel="Abort"
 *   confirmationMessage="Do you really want to remove this item?"
 * />
 */

import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, ReactNode } from 'react';

import { TrashIcon } from '~components/icons/TrashIcon';
import useLabels from '~hooks/useLabels';
import styles from './DeleteItemPopover.module.css';

interface DeleteItemPopoverProps {
  onDelete: () => void;
  icon?: ReactNode;
  deleteLabel?: string;
  cancelLabel?: string;
  confirmationMessage?: string;
  className?: string;
}

export function DeleteItemPopover({
  onDelete,
  icon = <TrashIcon />,
  deleteLabel,
  cancelLabel,
  confirmationMessage,
  className,
}: DeleteItemPopoverProps) {
  const { btnDeleteItemPopoverConfirm, btnDeleteItemPopoverCancel, msgDeleteItemPopoverConfirmation } = useLabels([
    'btnDeleteItemPopoverConfirm',
    'btnDeleteItemPopoverCancel',
    'msgDeleteItemPopoverConfirmation',
  ]);

  return (
    <Popover className={clsx('relative flex', className)}>
      {({ open, close }) => (
        <>
          <Popover.Button title={deleteLabel || btnDeleteItemPopoverConfirm} disabled={open}>
            {icon}
          </Popover.Button>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Popover.Panel className={clsx(styles.panel, 'absolute z-50 mt-3 transform right-0 top-0')}>
              <div className="block">
                <div>{confirmationMessage || msgDeleteItemPopoverConfirmation}</div>
                <div className="mt-4 flex justify-end">
                  <button type="button" onClick={() => close()} className={clsx(styles.button, styles.cancelButton)}>
                    {cancelLabel || btnDeleteItemPopoverCancel}
                  </button>
                  <div className="mx-2" />
                  <button
                    onClick={() => {
                      onDelete();
                      close();
                    }}
                    className={clsx(styles.button, styles.deleteButton)}
                  >
                    {deleteLabel || btnDeleteItemPopoverConfirm}
                  </button>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
